import enCommon from '../../locales/en/common';

import { Locale } from '@/constants/Locale';
import i18n from '@/plugins/i18n';
import { translateRuntimeKey } from '@/utils/runtimeTranslation';

interface CreateLocalizedTextOptions {
    value: string;
    language?: string;
}

export const LANGUAGE_NATIVE_NAMES: Record<string, string> = {
    en: 'English',
    fr: 'Français (Canada)',
    fr_FR: 'Français (France)',
    es: 'Español',
    vi: 'Tiếng Việt',
    tl: 'Tagalog',
    de: 'Deutsch',
    pt: 'Português',
    zh: '中文 (普通话)',
    hu: 'Magyar',
    it: 'Italiano',
    th: 'ไทย',
    tr: 'Türkçe',
    pt_BR: 'Português (Brasil)',
    ro: 'Română',
    cs: 'Čeština',
    he: 'עברית',
    sk: 'Slovenčina',
    km: 'ខ្មែរ',
    bg: 'Български',
    nl: 'Nederlands',
    pl: 'Polski',
    lv: 'Latviešu',
    lt: 'Lietuvių',
    et: 'Eesti',
    sr: 'Српски',
    sl: 'Slovenščina',
    hr: 'Hrvatski',
    zh_Hant: '繁體中文',
    ja: '日本語',
    sw: 'Kiswahili',
    ar: 'العربية',
};

export const getLanguageNativeName = (languageCode: string): string => {
    return LANGUAGE_NATIVE_NAMES[languageCode] ?? getLanguageDisplayName(languageCode);
};

export type LocalizedText = Record<string, string>;

type LanguageDictionary = Record<string, string>;

const fallbackLanguages: LanguageDictionary = enCommon.languages;

const getBundleLanguages = (language: string): LanguageDictionary | undefined => {
    if (typeof i18n.getResourceBundle !== 'function') {
        return undefined;
    }

    const bundle = i18n.getResourceBundle(language, 'translation') as
        | { common?: { languages?: LanguageDictionary } }
        | undefined;
    return bundle?.common?.languages;
};

export const getAvailableLanguages = (): LanguageDictionary => {
    return getBundleLanguages(i18n.language) ?? getBundleLanguages('en') ?? fallbackLanguages;
};

export const normalizeLanguageCode = (language: string): string => {
    const availableLanguageCodes = Object.keys(getAvailableLanguages());

    if (availableLanguageCodes.includes(language)) {
        return language;
    }

    const shortLanguageCode = language.split(/[-_]/)[0];
    if (availableLanguageCodes.includes(shortLanguageCode)) {
        return shortLanguageCode;
    }

    return availableLanguageCodes.includes('en') ? 'en' : (availableLanguageCodes[0] ?? language);
};

export const getLanguageDisplayName = (languageCode: string): string => {
    const languages = getAvailableLanguages();
    if (languages[languageCode]) {
        return languages[languageCode];
    }

    return translateRuntimeKey(`common.languages.${languageCode}`) ?? languageCode.toUpperCase();
};

let _companyDefaultLanguage = 'en';

export const setCompanyDefaultLanguage = (language: string): void => {
    _companyDefaultLanguage = language;
};

/**
 * Helper function to get text from a multilingual object or string, properly handling empty strings
 * @param names - The multilingual object or string
 * @param preferredLanguages - Array of language codes to try in order
 * @returns The text value or empty string if not found
 */
const getTextWithFallback = (
    names: LocalizedText | string | undefined,
    preferredLanguages: string[],
): string => {
    if (!names) {
        return '';
    }

    // Handle backward compatibility: if names is a string, return it directly
    if (typeof names === 'string') {
        return names;
    }

    for (const lang of preferredLanguages) {
        const preferredValue = names[lang];
        if (lang in names && (preferredValue ?? '').trim() !== '') {
            return preferredValue;
        }
        const shortLang = lang.split(/[-_]/)[0];
        const shortValue = names[shortLang];
        if (shortLang !== lang && shortLang in names && (shortValue ?? '').trim() !== '') {
            return shortValue;
        }
    }

    // Fallback to first available non-empty value in any language.
    return Object.values(names).find((name) => (name ?? '').trim() !== '') || '';
};

export const getTextByLanguage = (
    names: LocalizedText | string | undefined,
    language: string,
): string => {
    const currentLanguage = i18n.language;
    return getTextWithFallback(names, [language, currentLanguage]);
};

export const getEditableTextByLanguage = (
    names: LocalizedText | undefined,
    language: string,
): string => {
    if (names && language in names) {
        return names[language] ?? '';
    }

    return getTextByLanguage(names, language);
};

export const getLocalizedText = (names: LocalizedText | string | undefined): string => {
    const currentLanguage = i18n.language;
    return getTextWithFallback(names, [currentLanguage, _companyDefaultLanguage]);
};

/** True when the dictionary holds at least one non-empty (non-whitespace) value. */
export const hasLocalizedText = (text?: LocalizedText): boolean =>
    Object.values(text ?? {}).some((value) => (value ?? '').trim().length > 0);

/** Drops blank language entries so clearing one locale does not block other locales. */
export const stripEmptyLocalizedValues = (text?: LocalizedText): LocalizedText =>
    Object.fromEntries(
        Object.entries(text ?? {}).filter(([, value]) => (value ?? '').trim() !== ''),
    );

export const createLocalizedText = (options: CreateLocalizedTextOptions): LocalizedText => {
    const { value, language } = options;
    const lang = language ?? i18n.language;

    return Object.fromEntries(
        Object.keys(Locale).map((key) => {
            const localeValue = Locale[key as keyof typeof Locale];
            return [localeValue, localeValue === lang ? value : ''];
        }),
    );
};
